/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useMemo } from 'react'
import { css } from '@emotion/react'
import { App, Tooltip, Checkbox } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { ChevronDown, ChevronRight, ClockPlusIcon } from '../icons'
import type { VenueCardProps, TimeSlotEditState } from './types'
import { getVenueTimeSlotGroups } from './types'
import TimeSlotTableHeader from './TimeSlotTableHeader'
import TimeSlotRow from './TimeSlotRow'
import SurfaceSection from './SurfaceSection'

const iconFontStyle = { fontSize: 18 } as const

/**
 * Venue Card Component
 * Shows venue with expandable surfaces, each with their own time slots
 */
const VenueCard: React.FC<VenueCardProps> = ({
  venue,
  competitionId,
  timeSlots: allTimeSlots,
  timeSlotCounts,
  timeSlotsLoading,
  divisions,
  onEditVenue,
  onDeleteVenue,
  onAddTimeSlot,
  onUpdateTimeSlot,
  onDeleteTimeSlot,
  onDuplicateTimeSlot,
  getTimeSlotsForFacility,
  onAddTimeSlotForVenue,
  onUpdateTimeSlotBatch,
  onDeleteTimeSlotBatch: _onDeleteTimeSlotBatch,
}) => {
  const { message } = App.useApp()

  // Venue expanded state
  const [isVenueExpanded, setIsVenueExpanded] = useState(true)

  // Track which surfaces are expanded (all expanded by default)
  const [expandedSurfaces, setExpandedSurfaces] = useState<
    Record<string, boolean>
  >({})

  // Venue-level time slot editing state (for "Add Time Slot" on all surfaces)
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null)
  const [editState, setEditState] = useState<TimeSlotEditState | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([])

  // Convenience flag: is the venue-level "add slot" form open?
  const isAddingSlot = editingSlotId === 'new' && editState !== null

  // Group identical time slots across facilities (used for total count)
  const venueTimeSlotGroups = useMemo(
    () => getVenueTimeSlotGroups(allTimeSlots, venue),
    [allTimeSlots, venue]
  )

  // Calculate totals
  const totalTimeSlots = venueTimeSlotGroups.length
  const totalSurfaces = venue.facilities?.length || 0

  // Toggle venue expanded state
  const toggleVenue = useCallback(() => {
    setIsVenueExpanded((prev) => !prev)
  }, [])

  // Toggle individual surface expanded state
  const toggleSurface = useCallback((facilityId: string) => {
    setExpandedSurfaces((prev) => ({
      ...prev,
      [facilityId]: !(prev[facilityId] ?? true),
    }))
  }, [])

  // Facility IDs for this venue
  const facilityIds = useMemo(
    () => venue.facilities?.map((f) => f.id) || [],
    [venue.facilities]
  )

  // Start adding a new slot at venue level (fans out to all surfaces)
  const handleAddNewSlot = useCallback(() => {
    setIsVenueExpanded(true)
    setEditingSlotId('new')
    setEditState({
      slotId: null,
      frequency: 'weekly',
      days: [1, 3, 5],
      startTime: '09:00',
      endTime: '17:00',
      divisions: [],
    })
    setSelectedFacilityIds(venue.facilities?.map((f) => f.id) || [])
  }, [venue.facilities])

  // Save slot (create or update across all facilities)
  const handleSave = useCallback(async () => {
    if (!editState) {
      return
    }

    setSaveLoading(true)
    try {
      const data = {
        label: `Time Slot ${venueTimeSlotGroups.length + 1}`,
        recurrence_type: editState.frequency,
        days: editState.frequency === 'specific' ? [] : editState.days,
        time_ranges: [
          {
            start_time: editState.startTime,
            end_time: editState.endTime,
          },
        ],
        divisions: editState.divisions,
        slot_date:
          editState.frequency === 'specific'
            ? editState.slotDate || null
            : null,
      }

      if (editState.slotId) {
        // Find the group containing this slot and update all slots in it
        const group = venueTimeSlotGroups.find((g) =>
          g.slotIds.includes(editState.slotId!)
        )
        if (group) {
          await onUpdateTimeSlotBatch(group.slotIds, data)
        }
        message.success('Time slot updated across all surfaces')
      } else {
        // Validate at least one surface is selected
        if (selectedFacilityIds.length === 0) {
          message.warning('Select at least one surface')
          setSaveLoading(false)
          return
        }
        // Create for selected facilities in this venue
        await onAddTimeSlotForVenue(selectedFacilityIds, data)
        const total = facilityIds.length
        if (selectedFacilityIds.length === total) {
          message.success('Time slot created for all surfaces')
        } else {
          message.success(
            `Time slot created for ${selectedFacilityIds.length} of ${total} surfaces`
          )
        }
      }

      setEditingSlotId(null)
      setEditState(null)
      setSelectedFacilityIds([])
    } catch (err) {
      console.error('Failed to save time slot:', err)
      message.error('Failed to save time slot')
    } finally {
      setSaveLoading(false)
    }
  }, [
    editState,
    facilityIds,
    selectedFacilityIds,
    onAddTimeSlotForVenue,
    onUpdateTimeSlotBatch,
    venueTimeSlotGroups,
    message,
  ])

  // Cancel editing
  const handleCancel = useCallback(() => {
    setEditingSlotId(null)
    setEditState(null)
    setSelectedFacilityIds([])
  }, [])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid ${colors.SECONDARY_200};
      `}
    >
      {/* Venue Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background-color: ${colors.SECONDARY_300};
          cursor: pointer;
          user-select: none;

          &:hover {
            background-color: ${colors.SECONDARY_200};
          }
        `}
        onClick={toggleVenue}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          {/* Left section: expand/collapse + name */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              width: 319px;
              flex-shrink: 0;
            `}
          >
            <div
              css={css`
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              `}
            >
              {isVenueExpanded ? (
                <ChevronDown size={24} />
              ) : (
                <ChevronRight size={24} />
              )}
            </div>

            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 16px;
                font-weight: 500;
                line-height: 23.2px;
                color: ${colors.NEUTRAL_30};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {venue.name || venue.name_full}
            </span>
          </div>

          {/* Stats */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 20.3px;
                color: ${colors.NEUTRAL_50};
                white-space: nowrap;
              `}
            >
              {totalTimeSlots} Time Slot{totalTimeSlots !== 1 ? 's' : ''}
            </span>
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 20.3px;
                color: ${colors.NEUTRAL_50};
                width: 88px;
              `}
            >
              {totalSurfaces} Surface{totalSurfaces !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type='button'
            onClick={handleAddNewSlot}
            disabled={isAddingSlot}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 16px;
              background: ${colors.PRIMARY};
              border: none;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                background: ${colors.PRIMARY_HOVER};
              }

              &:disabled {
                opacity: 0.5;
                pointer-events: none;
              }
            `}
            aria-label='Add time slot'
          >
            <ClockPlusIcon size={24} color={PRIMARY_BUTTON_CONTENT.icon} />
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;
                line-height: 20.3px;
                color: ${PRIMARY_BUTTON_CONTENT.text};
              `}
            >
              Add Time Slot
            </span>
          </button>
          <Tooltip title='Remove Location' placement='top'>
            <button
              type='button'
              onClick={() => onDeleteVenue(venue)}
              css={css`
                background: transparent;
                border: none;
                color: ${colors.ERROR_200};
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;

                &:hover {
                  color: ${colors.ERROR_300};
                }
              `}
              aria-label={`Remove ${venue.name}`}
            >
              <DeleteOutlined style={iconFontStyle} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Venue Content */}
      {isVenueExpanded && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {venue.facilities && venue.facilities.length > 0 ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              {/* Venue-level new slot row (when adding to all surfaces) — shown at top */}
              {isAddingSlot && (
                <div
                  css={css`
                    background-color: ${colors.SECONDARY_400};
                    border-bottom: 2px solid ${colors.PRIMARY};
                    position: relative;
                    z-index: 3;
                    animation: slideDown 0.2s ease-out;

                    @keyframes slideDown {
                      from {
                        opacity: 0;
                        transform: translateY(-8px);
                      }
                      to {
                        opacity: 1;
                        transform: translateY(0);
                      }
                    }
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      padding: 6px 12px 2px;
                    `}
                  >
                    <ClockPlusIcon size={16} color={colors.PRIMARY} />
                    <span
                      css={css`
                        font-family: 'Inter', sans-serif;
                        font-size: 13px;
                        font-weight: 500;
                        color: ${colors.PRIMARY};
                      `}
                    >
                      Adding Time Slot to Location
                    </span>
                  </div>
                  <TimeSlotTableHeader />
                  <TimeSlotRow
                    timeSlot={{
                      id: 'new',
                      label: 'New Time Slot',
                      recurrence_type: editState.frequency,
                      is_active: true,
                      days: editState.days,
                      time_ranges: [
                        {
                          id: 'new',
                          start_time: editState.startTime,
                          end_time: editState.endTime,
                          sort_order: 0,
                        },
                      ],
                      facility_id: facilityIds[0] || '',
                      schedule_id: '',
                    }}
                    index={venueTimeSlotGroups.length}
                    isEditing={true}
                    editState={editState}
                    divisions={divisions}
                    onEdit={() => {}}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDelete={() => handleCancel()}
                    onEditStateChange={setEditState}
                    loading={saveLoading}
                  />
                  <div
                    css={css`
                      padding: 4px 12px;
                      background-color: ${colors.SECONDARY_400};
                      display: flex;
                      align-items: center;
                      gap: 12px;
                      flex-wrap: wrap;
                    `}
                  >
                    <span
                      css={css`
                        font-family: 'Inter', sans-serif;
                        font-size: 12px;
                        color: ${colors.NEUTRAL_100};
                        font-style: italic;
                      `}
                    >
                      Surfaces:
                    </span>
                    <button
                      type='button'
                      onClick={() => {
                        const allIds = venue.facilities?.map((f) => f.id) || []
                        setSelectedFacilityIds(
                          selectedFacilityIds.length === allIds.length
                            ? []
                            : allIds
                        )
                      }}
                      css={css`
                        background: none;
                        border: none;
                        color: ${colors.PRIMARY};
                        cursor: pointer;
                        font-family: 'Inter', sans-serif;
                        font-size: 12px;
                        padding: 0;
                        text-decoration: underline;

                        &:hover {
                          color: ${colors.PRIMARY_HOVER};
                        }
                      `}
                    >
                      {selectedFacilityIds.length ===
                      (venue.facilities?.length || 0)
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                    {venue.facilities?.map((facility) => (
                      <Checkbox
                        key={facility.id}
                        checked={selectedFacilityIds.includes(facility.id)}
                        onChange={(e) => {
                          setSelectedFacilityIds((prev) =>
                            e.target.checked
                              ? [...prev, facility.id]
                              : prev.filter((id) => id !== facility.id)
                          )
                        }}
                        css={css`
                          font-family: 'Inter', sans-serif;
                          font-size: 12px;
                          color: ${colors.NEUTRAL_30};

                          .ant-checkbox-checked .ant-checkbox-inner {
                            background-color: ${colors.PRIMARY};
                            border-color: ${colors.PRIMARY};
                          }
                        `}
                      >
                        {facility.name}
                      </Checkbox>
                    ))}
                    {selectedFacilityIds.length === 0 && (
                      <span
                        css={css`
                          font-family: 'Inter', sans-serif;
                          font-size: 12px;
                          color: ${colors.ERROR_200};
                          margin-left: auto;
                        `}
                      >
                        Select at least one surface
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Individual Surface Sections */}
              <div
                css={css`
                  position: relative;
                `}
              >
                {isAddingSlot && (
                  <div
                    css={css`
                      position: absolute;
                      inset: 0;
                      z-index: 2;
                      background: rgba(24, 29, 38, 0.75);
                      backdrop-filter: blur(2px);
                      pointer-events: all;
                      cursor: not-allowed;
                    `}
                  />
                )}
                {venue.facilities.map((facility) => {
                  const facilitySlots = getTimeSlotsForFacility(facility.id)
                  const slotCount = timeSlotCounts[facility.id] || 0
                  const totalCount = slotCount || facilitySlots.length
                  const isExpanded =
                    expandedSurfaces[facility.id] === undefined
                      ? true
                      : expandedSurfaces[facility.id]

                  return (
                    <SurfaceSection
                      key={facility.id}
                      facility={facility}
                      competitionId={competitionId}
                      timeSlots={facilitySlots}
                      timeSlotCount={totalCount}
                      isExpanded={isExpanded}
                      timeSlotsLoading={timeSlotsLoading}
                      divisions={divisions}
                      onToggle={() => toggleSurface(facility.id)}
                      onAddTimeSlot={onAddTimeSlot}
                      onUpdateTimeSlot={onUpdateTimeSlot}
                      onDeleteTimeSlot={onDeleteTimeSlot}
                      onDuplicateTimeSlot={onDuplicateTimeSlot}
                    />
                  )
                })}
              </div>
            </div>
          ) : (
            <div
              css={css`
                padding: 12px 20px;
                background-color: ${colors.SECONDARY_500};
                text-align: center;
              `}
            >
              <span
                css={css`
                  color: ${colors.NEUTRAL_100};
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                `}
              >
                No surfaces configured.{' '}
                <button
                  type='button'
                  onClick={() => onEditVenue(venue)}
                  css={css`
                    background: none;
                    border: none;
                    color: ${colors.PRIMARY};
                    cursor: pointer;
                    text-decoration: underline;
                    font-size: 13px;
                    padding: 0;

                    &:hover {
                      color: ${colors.PRIMARY_HOVER};
                    }
                  `}
                >
                  Edit venue to add surfaces.
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VenueCard
